<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { translate } from "@/common/gyldendal-helpers";
import { LEVEL_EXPERT } from "@/common/level-types";
import ActionWithTooltip from "@/components/ActionWithTooltip.vue";
import ArticleContent from "@/components/article/ArticleContent.vue";
import ArticleContentSynonyms from "@/components/article/ArticleContentSynonyms.vue";
import HeaderElement from "@/components/article/HeaderElement.vue";
import ImageButton from "@/components/article/ImageButton.vue";
import InflectionSchemeLink from "@/components/article/InflectionSchemeLink.vue";
import AudioButton from "@/components/AudioButton.vue";
import IconWhiteChevronDown from "@/components/icons/white/IconWhiteChevronDown.vue";
import TouchOrMouseElement from "@/components/TouchOrMouseElement.vue";
import vLang from "@/directives/lang";
import { MatomoHelper } from "@/helpers";
import { useSearchStore, useSettingsStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";
import type { GyldendalArticle } from "@/types/article-formats/gyldendal";

import ArticleEtymology from "./ArticleEtymology.vue";
import ArticleMeanings from "./ArticleMeanings.vue";
import ArticleReferences from "./ArticleReferences.vue";
import ArticleRelations from "./ArticleRelations.vue";
import ArticleShortViewItem from "./ArticleShortViewItem.vue";
import ArticleVariantForms from "./ArticleVariantForms.vue";
import AdjectiveTooltip from "./wordclassTooltips/AdjectiveTooltip.vue";
import AdverbTooltip from "./wordclassTooltips/AdverbTooltip.vue";
import ConjoinedTooltip from "./wordclassTooltips/ConjoinedTooltip.vue";
import InterjectionTooltip from "./wordclassTooltips/InterjectionTooltip.vue";
import NounTooltip from "./wordclassTooltips/NounTooltip.vue";
import NumeralTooltip from "./wordclassTooltips/NumeralTooltip.vue";
import PrepositionTooltip from "./wordclassTooltips/PrepositionTooltip.vue";
import PronounTooltip from "./wordclassTooltips/PronounTooltip.vue";
import VerbTooltip from "./wordclassTooltips/VerbTooltip.vue";

interface Props {
  article: GyldendalArticle;
  hasReadingLines: boolean;
  showImageByDefault: boolean;
  isFuzzy: boolean;
  isSynonyms: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isSynonyms: false
});

const { t } = useI18n();
const searchStore = useSearchStore();
const settingsStore = useSettingsStore();

const activeIllustrationUrl = ref<string>("");
const headerHover = ref<boolean>(true);
const isExpanded = ref<boolean>(false); // isExpanded should only be used as underlying state for the isContentExpanded property

const searchDictionaryId = computed(() => searchStore.searchDictionaryId);
const searchLevel = computed(() => searchStore.adjustedSearchLevel);
const gyldendalArticleTranslationsExpandedDefault = computed(
  () => settingsStore.userPreferences.gyldendalArticleTranslationsExpandedDefault
);

const canCollapse = computed(() => {
  if (props.isFuzzy) {
    return true;
  }

  if (props.isSynonyms) {
    return false;
  }

  if (searchDictionaryId.value) {
    return false;
  }

  if (searchLevel.value !== LEVEL_EXPERT) {
    return false;
  }

  return props.article.shortViewItems?.length > 0;
});

const isContentExpanded = computed({
  get() {
    if (!canCollapse.value) {
      return true;
    }

    return isExpanded.value;
  },
  set(newValue) {
    isExpanded.value = newValue;
  }
});

const pronunciationSoundFile = computed(() => props.article.lemma.pronunciations[0]?.soundFile);

const hasInflectionData = computed(() => (props.article.lemma.inflectionTable?.length ?? 0) > 0);

const wordclassTooltipComponent = computed(
  () =>
    ({
      adjective: AdjectiveTooltip,
      adverb: AdverbTooltip,
      conjunction: ConjoinedTooltip,
      preposition: PrepositionTooltip,
      pronoun: PronounTooltip,
      noun: NounTooltip,
      numeral: NumeralTooltip,
      interjection: InterjectionTooltip,
      verb: VerbTooltip,
      "": null
    })[props.article.body.lemmaWordclass ?? ""]
);

const wordclassTranslated = computed(() =>
  translate(props.article.body.lemmaWordclass, props.article.sourceLanguage)
);

const lemmaInflectionFormsTranslated = computed(
  () =>
    props.article.body.lemmaInflectionForms.map((inflectionForm: string) =>
      translate(inflectionForm, props.article.sourceLanguage)
    ) ?? []
);

const lemmaTypesTranslated = computed(() =>
  props.article.lemma.types.map((type: string) => translate(type, props.article.sourceLanguage))
);

watch(
  () => props.article.body.id,
  () => {
    if (props.article.type !== "article") {
      activeIllustrationUrl.value = "";
      return;
    }

    const illustrations = props.article.lemma.illustrations;
    if (props.showImageByDefault && illustrations.length > 0) {
      activeIllustrationUrl.value = illustrations[0];
    } else {
      activeIllustrationUrl.value = "";
    }
  },
  { immediate: true }
);

onMounted(() => {
  // Set default expanded state
  isContentExpanded.value = gyldendalArticleTranslationsExpandedDefault.value;
});

const disableHeaderHover = () => {
  headerHover.value = false;
};

const enableHeaderHover = () => {
  headerHover.value = true;
};

const onToggleExpand = () => {
  if (!canCollapse.value) {
    return;
  }

  isContentExpanded.value = !isContentExpanded.value;
};

const onIllustrationUpdate = (url: string) => {
  activeIllustrationUrl.value = url;

  if (activeIllustrationUrl.value !== "") {
    isContentExpanded.value = true;
  }
};

const trackPronunciationClick = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Results,
    MatomoEventAction.ClickPronunciation,
    "Pronunciation on translation"
  );
};

const trackInflectionSchemeClick = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Results,
    MatomoEventAction.ClickInflectionScheme,
    "Inflection scheme on translation"
  );
};
</script>

<template>
  <ArticleContent
    v-if="!props.isSynonyms || !props.article.hideSynonyms"
    :class="{ 'article-content--chained': !isContentExpanded }"
  >
    <div
      class="article-content__header d-flex flex-lg-row flex-column justify-content-between"
      :class="{ 'article-content__header--with-hover': canCollapse && headerHover }"
      @click.prevent.stop="onToggleExpand()"
    >
      <div class="article-header">
        <button
          v-if="canCollapse"
          class="article-content__drop-down mr-3"
          :class="{
            'article-content__drop-down--expanded': isContentExpanded
          }"
          :aria-expanded="isContentExpanded"
          :aria-label="isContentExpanded ? t('article.contract') : t('article.expand')"
          @click.prevent.stop="onToggleExpand()"
        >
          <IconWhiteChevronDown role="presentation" class="article-content__drop-down-icon" />
        </button>
        <h5 v-lang:source class="article-header__word">
          <span v-html="article.body.headword"></span
          ><sup v-if="article.body.lemmaTrademark">&reg;</sup>
        </h5>
        <template v-if="article.type === 'article' || props.isSynonyms">
          <HeaderElement v-lang:source :content="article.body.lemmaAboutHeadword" />
          <HeaderElement v-lang:source :content="article.body.lemmaInflectedForm" />

          <template v-if="article.body.lemmaWordclass">
            <span class="ml-3"></span>

            <Component
              :is="wordclassTooltipComponent"
              v-if="wordclassTooltipComponent"
              @mouseover="disableHeaderHover()"
              @mouseout="enableHeaderHover()"
            />

            <span v-else class="article-header__grammar">{{ wordclassTranslated }}</span>
          </template>

          <HeaderElement
            v-lang:source
            :content="lemmaInflectionFormsTranslated.join(', ')"
            :wrapped="false"
            class="article-header__grammar"
          />
          <HeaderElement v-lang:source :content="lemmaTypesTranslated.join(', ')" />
          <HeaderElement v-lang:source :content="article.lemma.description" />
          <HeaderElement
            v-if="article.lemma.description !== article.body.lemmaDescription"
            v-lang:source
            :content="article.body.lemmaDescription"
          />
          <HeaderElement
            v-lang:source
            :content="article.body.lemmaAbbreviationFor"
            prefix="forkortelse for: "
          />
          <HeaderElement
            v-lang:source
            :content="article.body.lemmaAbbreviated"
            prefix="forkortes: "
          />
        </template>
      </div>

      <div v-if="article.type === 'article' || props.isSynonyms" class="article-header__controls">
        <TouchOrMouseElement class="d-inline-flex">
          <template #mouse>
            <ActionWithTooltip
              v-if="pronunciationSoundFile"
              hide-on-click
              class="article-header__btn"
            >
              <template #element>
                <AudioButton
                  :audio-url="pronunciationSoundFile"
                  :aria-label="t('article.pronunciation.label')"
                  @mouseover="disableHeaderHover()"
                  @mouseout="enableHeaderHover()"
                  @click="trackPronunciationClick()"
                />
              </template>
              <template #actionInitial>{{ t("article.pronunciation.label") }}</template>
            </ActionWithTooltip>
            <ImageButton
              v-if="!props.isSynonyms"
              :illustrations="article.lemma.illustrations"
              button-class="article-header__btn"
              :current-active-illustration-url="activeIllustrationUrl"
              @illustration-url-update="onIllustrationUpdate($event)"
              @mouseover="disableHeaderHover()"
              @mouseout="enableHeaderHover()"
            />
            <ActionWithTooltip v-if="hasInflectionData" hide-on-click class="article-header__btn">
              <template #element>
                <InflectionSchemeLink
                  :word="article.body.headword"
                  :article-id="article.body.id"
                  @mouseover="disableHeaderHover()"
                  @mouseout="enableHeaderHover()"
                  @click="trackInflectionSchemeClick()"
                />
              </template>
              <template #actionInitial>{{ t("article.actions.conjugation.open") }}</template>
            </ActionWithTooltip>
          </template>
          <template #touch>
            <AudioButton
              v-if="pronunciationSoundFile"
              :audio-url="pronunciationSoundFile"
              :aria-label="t('article.pronunciation.label')"
              class="article-header__btn"
              @mouseover="disableHeaderHover()"
              @mouseout="enableHeaderHover()"
              @click="trackPronunciationClick()"
            />
            <ImageButton
              v-if="!props.isSynonyms"
              :illustrations="article.lemma.illustrations"
              button-class="article-header__btn"
              :current-active-illustration-url="activeIllustrationUrl"
              @illustration-url-update="onIllustrationUpdate($event)"
              @mouseover="disableHeaderHover()"
              @mouseout="enableHeaderHover()"
            />
            <InflectionSchemeLink
              v-if="hasInflectionData"
              :word="article.body.headword"
              :article-id="article.body.id"
              class="article-header__btn"
              @mouseover="disableHeaderHover()"
              @mouseout="enableHeaderHover()"
              @click="trackInflectionSchemeClick()"
            />
          </template>
        </TouchOrMouseElement>
      </div>
    </div>

    <div v-if="props.isSynonyms">
      <ArticleContentSynonyms
        class="article-content__body-container article-content__body-container--reading-lines px-3"
        preferred-source="gd"
        :synonyms="article.synonyms"
      />
    </div>

    <div v-else-if="isContentExpanded">
      <div
        v-if="article.body.variantForms.length > 0"
        class="article-content__body-container d-flex flex-column flex-lg-row"
        :class="{ 'article-content__body-container--reading-lines': hasReadingLines }"
      >
        <div class="indentation-container flex-grow-1 flex-shrink-1">
          <ArticleVariantForms :variant-forms="article.body.variantForms" />
        </div>
      </div>
      <div
        v-if="article.body.lemmaInflectionTable"
        class="inflection-scheme__table table-responsive p-3"
        v-html="article.body.lemmaInflectionTable"
      ></div>
      <div
        class="article-content__body-container d-flex flex-column flex-lg-row"
        :class="{ 'article-content__body-container--reading-lines': hasReadingLines }"
      >
        <div class="indentation-container flex-grow-1 flex-shrink-1">
          <ArticleReferences :references="article.body.references" />

          <p v-if="article.body.lemmaShortformFor" class="article-content__text">
            <span class="article-content__label">Kortform for:</span>
            <span v-lang:source v-html="article.body.lemmaShortformFor"></span>
          </p>

          <p
            v-if="article.body.description || article.body.productiveDescription"
            v-lang:source
            class="article-content__text"
          >
            (<span v-html="article.body.description ?? article.body.productiveDescription"></span>)
          </p>

          <ArticleMeanings :meanings="article.meanings" :article-id="article.body.id" />

          <div
            v-if="article.body.relations.length > 0 || article.body.etymology"
            class="article-content__reading-line"
          >
            <ArticleRelations :relations="article.body.relations" />

            <ArticleEtymology v-if="article.body.etymology" :etymology="article.body.etymology" />
          </div>
        </div>
        <div
          v-if="activeIllustrationUrl"
          class="article-content__image-container d-flex align-items-start justify-content-center"
        >
          <img
            class="article-content__image img-fluid"
            :src="activeIllustrationUrl"
            :alt="
              t('article.illustration.alt', {
                word: article.body.headword
              })
            "
          />
        </div>
      </div>
    </div>
    <div v-else class="article-content__body-container">
      <div class="article-content__compact-body indentation-container">
        <template v-for="(item, index) in props.article.shortViewItems" :key="index">
          <ArticleShortViewItem :item="item" />
          <span v-if="index !== props.article.shortViewItems.length - 1">, </span>
        </template>
      </div>
    </div>
  </ArticleContent>
</template>
